import { isSameDay } from 'date-fns';
import { ProductProps } from '@/types';
import { getOnStorage } from '@/shared';
import * as ordersStore from '@/shared/ordersStore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchAxios, useNetworkStatusContext, useTables, useUserContext } from '@/hooks';

export interface OrderProps {
  clientName: string;
  clientRefNumber: string;
  clientCnpj: string;
  orders: OrderItemsProps;
}
export interface OrderItemsProps {
  orderNumber: string;
  erpNumberClient?: string;
  erpNumber: string;
  order_number_origem?: string;
  origin: string;
  orderNumberMagento: string;
  quantity: number | string;
  status: string;
  isLocal?: boolean;
  reason: string;
  orderDate: string;
  total: string;
  vlr_comissao?: number;
  paymentTerms: string;
  actionsPending: ItemsProps[];
}
export interface ItemsProps extends ProductProps {
  cod_segmento: number;
  sku: string;
  product: string;
  price: string;
  unitPrice: string;
  unitPricePerItem: string;
  stPrice: string;
  stPricePackaging: number;
  integrationPrice: number;
  integrationPriceFlex: number;
  quantity: number;
  quantityMultiplo: number;
  percentualFlex?: string;
  packaging: string;
  status: string;
  reason: string;

  unilever?: boolean;
  price_flex?: number;
  st_price_flex?: number;
  unit_price_flex?: number;
  unit_price_per_item_flex?: number;
}

export const useOrderStatusPage = () => {
  const { tables } = useTables();
  const isLoadedSynchedOrders = useRef(false);
  const { axiosCtx: axiosInstance } = useFetchAxios();
  const { customer, sendingOrders } = useUserContext();
  const { connectivityStatus } = useNetworkStatusContext();

  const isLoading = useRef(false);
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState<Date>();
  const [error, setError] = useState<Error | null>(null);
  const [orders, setOrders] = useState<OrderProps[]>([]);
  const [offlineOrders, setOfflineOrders] = useState<OrderProps[]>([]);
  const [products, setProducts] = useState<Record<number, ProductProps>>({});
  const [synchedOrdersList, setSynchedOrdersList] = useState<OrderProps[]>([]);

  const isLoadedProducts = useMemo(() => Object.keys(products || {})?.length, [products]);

  const synchedOrders = getOnStorage('synched_orders') || [];
  const offlineOrdersStr = getOnStorage('offline_orders') || [];

  useEffect(() => {
    if (data?.length && !isLoadedSynchedOrders.current) {
      isLoadedSynchedOrders.current = true;

      // Alguns ambientes retornam `order_number_origem` (pt-BR) e outros `order_number_origin` (en).
      // Tornamos a checagem resiliente a ambos para localizar o pedido local correto.
      // BUG-001: legacy code wrote a filtered `synched_orders` array directly to
      // localStorage, racing with ordersStore's write-through projection and
      // wiping the offline-browsable list. We now drive the store: each order
      // the ERP returned moves to terminal state=confirmed and the projection
      // is recomputed exactly once per mutation.
      const originIds = data
        .map(
          (item: any) =>
            item?.orders?.order_number_origem ||
            item?.orders?.order_number_origin ||
            item?.orders?.order_number
        )
        .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);

      (async () => {
        for (const id of originIds) {
          try {
            await ordersStore.markConfirmed(id);
          } catch {
            // markConfirmed on a missing record is a no-op; any other error
            // we swallow so a single bad id doesn't block the rest.
          }
        }
      })();
    }
  }, [data]);

  useEffect(() => {
    // BUG-001: don't hammer the network when offline — without this guard,
    // every render while offline re-fired the fetch and kept the page in a
    // perceived "trying online" state, hiding the offline projection.
    if (loading || data || isLoadedProducts || isLoading.current || connectivityStatus !== 'online') return;

    isLoading.current = true;
  
    const rca = getOnStorage('selected_rca');
  
    const getCodRcasFromClients = (clients: any[] = [], selectedRca?: any) => {
      const codRcas = clients.flatMap((client) =>
        client.lojas?.map((loja: any) => loja.COD_RCA_CLIENTE) || []
      );
  
      if (selectedRca?.codPessoa) {
        codRcas.push(selectedRca.codPessoa);
      }
  
      return Array.from(
        new Set(
          codRcas
            .map(Number)
            .filter(Number.isFinite)
        )
      );
    };
  
    const fetchData = async () => {
      try {
        setLoading(true);
  
        const clients = tables.current.cliente || [];
  
        const codRcas = getCodRcasFromClients(clients, rca);
  
        const response = await axiosInstance.get(
          `${window.agp_config.REACT_APP_AWS}/pedido-notifica-get?seqpessoa=${codRcas.join(',')}`
        );
  
        const mergedPedidos = response.data?.pedidos || [];
  
        const productsDB = tables.current.dicionario_produtos;
  
        setProducts(productsDB);
        setData(mergedPedidos);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
        isLoading.current = false;
      }
    };
  
    fetchData();
  }, [axiosInstance, customer.codPessoa, data, loading, isLoadedProducts, connectivityStatus]);

  const mountOrders = useCallback(
    (orders: any, isLocal?: boolean) => {
      return orders?.map((order: any) => ({
        clientName: order?.company?.name,
        clientRefNumber: order?.company?.id,
        clientCnpj: order?.company?.cnpj,
        orders: {
          isLocal,
          orderNumber: order?.orders?.order_number,
          erpNumber: order?.orders?.erp_order_number,
          orderNumberMagento: order?.orders?.order_number_magento,
          quantity: order?.orders?.items,
          status: order?.orders?.status || 'indisponível',
          reason: order?.orders?.reason,
          orderDate: order?.orders?.order_date,
          total: order?.orders?.total,
          vlr_comissao: order?.orders?.vlr_comissao,
          paymentTerms: order?.orders?.payment_terms,
          actionsPending: order?.orders?.actions_pending?.map((item: any) => ({
            ...item,
            cod_segmento: item?.cod_segmento,
            sku: item?.sku,
            product: item?.product,
            price: item?.price,
            unitPrice: item?.unit_price,
            unitPricePerItem: item?.unit_price_per_item,
            stPrice: item?.st_price,
            stPricePackaging: item?.st_price_packaging,
            integrationPrice: item?.integration_price,
            integrationPriceFlex: item?.integration_price_flex,
            quantity: item?.quantity,
            quantityMultiplo: item?.quantity_multiplo,
            packaging: item?.packaging,
            percentualFlex: item?.percentual_flex,
            status: '',
            reason: '',

            unit_price_flex: item?.unit_price_flex,
            unit_price_per_item_flex: item?.unit_price_per_item_flex,
            st_price_flex: item?.st_price_flex,
            price_flex: item?.price_flex,
            unilever: item?.UNILEVER,

            ...products[item?.sku],
          })),
        },
      }));
    },
    [products]
  );

  const fetchOfflineData = () => {
    if (offlineOrdersStr && !offlineOrders?.length) {
      const ordersOffline = mountOrders(offlineOrdersStr, true);
      setOfflineOrders(ordersOffline);
    }

    if (synchedOrders && !synchedOrdersList?.length) {
      const filteredSynchedOrders = synchedOrders?.filter((order: any) => order?.company?.Cod_rca == customer?.codPessoa);
      const synchedList = mountOrders(filteredSynchedOrders, true);
      setSynchedOrdersList(synchedList);
    }
  };

  useEffect(() => {
    if (sendingOrders) return;
    fetchOfflineData();
  }, [sendingOrders]);

  useEffect(() => {
    if (!isLoadedProducts || connectivityStatus === 'offline') return;

    const orders = mountOrders(data);
    setOrders(orders);
  }, [mountOrders, data, isLoadedProducts, connectivityStatus]);

  const ordersDate = useMemo(() => {
    return orders?.map((order: any) => {
      const dateString = order?.orders?.orderDate?.replace(/^[^,]*,\s*/, '');
      return new Date(`${dateString.split('/').reverse().join('-')}T00:00:00`);
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!orders?.length) return [];

    if (!filterDate) return orders;

    return orders.filter((order) => {
      const orderDate = new Date(
        `${order?.orders?.orderDate
          ?.replace(/^[^,]*,\s*/, '')
          .split('/')
          .reverse()
          .join('-')}T00:00:00`
      );
      return isSameDay(orderDate, filterDate);
    });
  }, [orders, filterDate]);

  const filteredSynchedOrdersList = useMemo(() => {
    if (!synchedOrdersList?.length) return [];

    return synchedOrdersList?.filter(
      (syn) => !filteredOrders.find((filOrd) => filOrd.orders.orderNumber.includes(syn.orders.orderNumber))
    );
  }, [synchedOrdersList, filteredOrders]);

  return {
    error,
    loading,
    filterDate,
    ordersDate,
    ordersList: filteredOrders,
    synchedOrdersList: filteredSynchedOrdersList,
    offlineOrdersList: offlineOrders?.length ? offlineOrders : [],
    setFilterDate,
  };
};
